import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const getUserId = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'user_id') {
      return value;
    }
  }
  return null;
};

const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const foodClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

foodClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const userId = getUserId();
  if (userId && config.headers) {
    config.headers['X-User-ID'] = userId;
  }
  return config;
});

foodClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

// --- Zones ---

export interface RestaurantZone {
  id: string;
  name: string;
  description: string;
  code: string;
  is_active: boolean;
  opening_time: string;
  closing_time: string;
  min_capacity: number;
  max_capacity: number;
  sort_order: number;
  created_at: string;
}

export const zoneService = {
  list: (params?: Record<string, unknown>) =>
    foodClient.get<{
      data: RestaurantZone[];
      page: number;
      page_size: number;
      total_items: number;
      total_pages: number;
    }>('/api/v1/zones', { params }),
  getAll: async (): Promise<RestaurantZone[]> => {
    const res = await foodClient.get<RestaurantZone[]>('/api/v1/zones/all');
    return res.data;
  },
  create: (data: Partial<RestaurantZone>) => foodClient.post<RestaurantZone>('/api/v1/zones', data),
  update: (id: string, data: Partial<RestaurantZone>) =>
    foodClient.put<RestaurantZone>(`/api/v1/zones/${id}`, data),
  delete: (id: string) => foodClient.delete(`/api/v1/zones/${id}`),
};

// --- Tables ---

export type TableStatus = 'available' | 'reserved' | 'occupied' | 'cleaning';

export interface RestaurantTable {
  id: string;
  zone_id: string;
  zone?: RestaurantZone;
  table_number: string;
  display_name: string;
  capacity: number;
  min_capacity: number;
  max_capacity: number;
  status: TableStatus;
  is_active: boolean;
  shape: string;
  width: number;
  height: number;
  can_combine: boolean;
  sort_order: number;
  notes: string;
  created_at: string;
}

export const tableService = {
  list: (params?: { status?: string }) =>
    foodClient.get<{
      data: RestaurantTable[];
      page: number;
      page_size: number;
      total_items: number;
      total_pages: number;
    }>('/api/v1/tables', { params }),
  getAvailable: async (): Promise<RestaurantTable[]> => {
    const res = await foodClient.get<RestaurantTable[]>('/api/v1/tables/available');
    return res.data;
  },
  get: (id: string) => foodClient.get<RestaurantTable>(`/api/v1/tables/${id}`),
  create: (data: Partial<RestaurantTable>) =>
    foodClient.post<RestaurantTable>('/api/v1/tables', data),
  update: (id: string, data: Partial<RestaurantTable>) =>
    foodClient.put<RestaurantTable>(`/api/v1/tables/${id}`, data),
  updateStatus: (id: string, status: TableStatus) =>
    foodClient.put(`/api/v1/tables/${id}/status`, { status }),
};

// --- Orders ---

export type FoodOrderType = 'dine_in' | 'takeout' | 'delivery';
export type FoodOrderStatus =
  | 'pending'
  | 'confirmed'
  | 'preparing'
  | 'ready'
  | 'served'
  | 'completed'
  | 'cancelled';
export type FoodPaymentStatus =
  | 'pending'
  | 'partially_paid'
  | 'paid'
  | 'refunded'
  | 'failed'
  | 'cancelled'
  | 'charged_to_room';

export interface ItemModifier {
  name: string;
  price: number;
}

export interface FoodOrderItem {
  id: string;
  order_id: string;
  product_id: string | null;
  product_name: string;
  product_sku: string;
  quantity: number;
  unit_price: number;
  cost_price: number;
  tax_rate: number;
  tax_amount: number;
  discount_amount: number;
  total_price: number;
  modifiers: ItemModifier[];
  preparation_notes: string;
  status: string;
  department: string;
  is_prepared: boolean;
  sort_order: number;
  created_at: string;
  prepared_at: string | null;
  served_at: string | null;
  updated_at: string;
}

export interface FoodOrder {
  id: string;
  order_number: string;
  order_type: FoodOrderType;
  status: FoodOrderStatus;
  guest_id: string | null;
  booking_id: string | null;
  table_id: string | null;
  table?: RestaurantTable;
  reservation_id: string | null;
  guest_count: number;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  service_charge: number;
  total_amount: number;
  payment_status: FoodPaymentStatus;
  paid_amount: number;
  currency: string;
  guest_name: string;
  guest_phone: string;
  delivery_address: string;
  delivery_notes: string;
  notes?: string;
  special_requests: string;
  kitchen_notes: string;
  confirmed_at: string | null;
  prep_started_at: string | null;
  ready_at: string | null;
  served_at: string | null;
  completed_at: string | null;
  paid_at: string | null;
  cancellation_reason: string;
  cancelled_by_email: string;
  assigned_server: string | null;
  assigned_rider_id: string | null;
  delivery_status: string | null;
  estimated_delivery_at: string | null;
  actual_delivery_at: string | null;
  created_by: string;
  completed_by: string | null;
  merged_order_ids: string[];
  has_merged: boolean;
  deleted_at: string | null;
  items: FoodOrderItem[];
  items_total: number;
  items_served: number;
  items_pending: number;
  items_cancelled: number;
  created_at: string;
  updated_at: string;
  metadata?: {
    prices_include_vat?: boolean;
    payment?: {
      transaction_id?: string;
      method?: string;
      amount?: number;
    };
  };
}

export interface FoodOrderEvent {
  id: string;
  order_id: string;
  event_type: string;
  actor_id: string | null;
  from_user_id: string | null;
  to_user_id: string | null;
  created_at: string;
}

export interface CreateOrderItemInput {
  product_id?: string;
  product_name: string;
  product_sku?: string;
  quantity: number;
  unit_price: number;
  cost_price?: number;
  tax_rate?: number;
  discount_amount?: number;
  modifiers?: ItemModifier[];
  preparation_notes?: string;
  department?: string;
}

export interface CreateOrderInput {
  order_type: FoodOrderType;
  booking_id?: string;
  table_id?: string;
  table_number?: string;
  guest_count?: number;
  guest_name?: string;
  guest_phone?: string;
  delivery_address?: string;
  delivery_notes?: string;
  special_requests?: string;
  kitchen_notes?: string;
  notes?: string;
  items: CreateOrderItemInput[];
}

export interface UpdateOrderInput {
  guest_count?: number;
  guest_name?: string;
  guest_phone?: string;
  delivery_address?: string;
  delivery_notes?: string;
  special_requests?: string;
  kitchen_notes?: string;
}

export interface PaginatedOrders {
  data: FoodOrder[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface OrderLifecycleSummary {
  total_orders: number;
  open_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  paid_orders: number;
  served_unpaid_orders: number;
  handoff_orders: number;
  gross_sales: number;
  paid_sales: number;
  avg_prep_start_minutes: number;
  avg_prep_completion_minutes: number;
  avg_ready_to_served_minutes: number;
  avg_served_to_completed_minutes: number;
  avg_payment_minutes: number;
  avg_lifecycle_minutes: number;
}

export interface OrderLifecycleRow {
  order_id: string;
  order_number: string;
  order_type: FoodOrderType;
  status: FoodOrderStatus;
  payment_status: FoodPaymentStatus;
  created_by: string;
  assigned_server: string;
  completed_by: string;
  total_amount: number;
  paid_amount: number;
  item_count: number;
  items_served: number;
  items_cancelled: number;
  is_handoff: boolean;
  created_at: string;
  confirmed_at: string | null;
  prep_started_at: string | null;
  ready_at: string | null;
  served_at: string | null;
  completed_at: string | null;
  paid_at: string | null;
  prep_start_minutes: number;
  prep_completion_minutes: number;
  ready_to_served_minutes: number;
  served_to_completed_minutes: number;
  payment_minutes: number;
  lifecycle_minutes: number;
}

export interface StaffLifecycleSummary {
  user_id: string;
  orders_created: number;
  orders_served: number;
  orders_completed: number;
  handoffs_handled: number;
  revenue_completed: number;
  avg_ready_to_served_mins: number;
}

export interface ItemLifecycleSummary {
  total_items: number;
  total_served: number;
  total_not_served: number;
  total_cancelled: number;
  waiting_on_waiter: number;
  waiting_on_kitchen: number;
  served_unpaid_orders: number;
  avg_prep_minutes: number;
  avg_serve_minutes: number;
  avg_total_minutes: number;
  avg_chef_pickup_minutes: number | null;
  avg_cook_minutes: number | null;
  avg_waiter_minutes: number | null;
  avg_payment_minutes: number | null;
  avg_order_prep_minutes: number | null;
}

export interface OrderItemLifecycleRow {
  order_id: string;
  order_number: string;
  order_status: string;
  guest_name: string;
  table_number: string;
  payment_status: string;
  departments: string;
  order_created_at: string;
  item_count: number;
  items_served: number;
  items_not_served: number;
  items_cancelled: number;
  items_ready: number;
  avg_prep_minutes: number;
  avg_serve_minutes: number;
  avg_total_minutes: number;
  order_prep_minutes: number | null;
  avg_chef_pickup_minutes: number | null;
  avg_cook_minutes: number | null;
  avg_waiter_minutes: number | null;
  payment_minutes: number | null;
  service_to_payment_minutes: number | null;
}

export interface ItemLifecycleDetail {
  item_id: string;
  product_name: string;
  quantity: number;
  department: string;
  status: string;
  order_id: string;
  order_number: string;
  created_at: string;
  prepared_at: string | null;
  served_at: string | null;
  prep_started_at: string | null;
  prep_minutes: number;
  serve_minutes: number;
  total_minutes: number;
  chef_pickup_minutes: number | null;
  cook_minutes: number | null;
  waiter_pickup_minutes: number | null;
  payment_minutes: number | null;
}

export interface ItemLifecycleReport {
  summary: ItemLifecycleSummary;
  orders: OrderItemLifecycleRow[];
  items: ItemLifecycleDetail[];
}

export interface OrderLifecycleReport {
  summary: OrderLifecycleSummary;
  orders: OrderLifecycleRow[];
  staff: StaffLifecycleSummary[];
}

export const orderService = {
  list: (params?: { page?: number; page_size?: number }) =>
    foodClient.get<PaginatedOrders>('/api/v1/orders', { params }),
  get: (id: string) => foodClient.get<FoodOrder>(`/api/v1/orders/${id}`),
  create: (data: CreateOrderInput) => foodClient.post<FoodOrder>('/api/v1/orders', data),
  update: (id: string, data: UpdateOrderInput) =>
    foodClient.put<FoodOrder>(`/api/v1/orders/${id}`, data),
  delete: (id: string) => foodClient.delete(`/api/v1/orders/${id}`),
  confirm: (id: string) => foodClient.post(`/api/v1/orders/${id}/confirm`),
  startPrep: (id: string) => foodClient.post(`/api/v1/orders/${id}/prep`),
  markReady: (id: string) => foodClient.post(`/api/v1/orders/${id}/ready`),
  markServed: (id: string) => foodClient.post(`/api/v1/orders/${id}/served`),
  complete: (id: string) => foodClient.post(`/api/v1/orders/${id}/complete`),
  cancel: (id: string, reason?: string) =>
    foodClient.post(`/api/v1/orders/${id}/cancel`, { reason }),
  refund: (id: string, reason: string) =>
    foodClient.post(`/api/v1/orders/${id}/refund`, { reason }),
  split: (id: string, itemIds: string[]) =>
    foodClient.post<FoodOrder>(`/api/v1/orders/${id}/split`, { item_ids: itemIds }),
  applyDiscount: (id: string, data: { discount_type: string; value: number; code?: string }) =>
    foodClient.post<FoodOrder>(`/api/v1/orders/${id}/discount`, data),
  updateDeliveryStatus: (id: string, data: { status: string; rider_id?: string }) =>
    foodClient.patch<FoodOrder>(`/api/v1/orders/${id}/delivery-status`, data),
  processPayment: (id: string, phone: string) =>
    foodClient.post<FoodOrder>(`/api/v1/orders/${id}/pay`, { phone }),
  markAsPaid: (
    id: string,
    data: {
      amount: number;
      method: string;
      transactionId?: string;
      phone?: string;
      tipAmount?: number;
    },
  ) =>
    foodClient.post<FoodOrder>(`/api/v1/orders/${id}/pay`, {
      amount: data.amount,
      method: data.method,
      transaction_id: data.transactionId || '',
      phone: data.phone || '',
      tip_amount: data.tipAmount || 0,
    }),
  chargeToRoom: (id: string) =>
    foodClient.post<FoodOrder>(`/api/v1/orders/${id}/pay`, {
      amount: 0,
      method: 'room_charge',
      transaction_id: '',
      phone: '',
    }),
  updateItemStatus: (itemId: string, status: string) =>
    foodClient.put<FoodOrderItem>(`/api/v1/orders/items/${itemId}/status`, { status }),
  addItem: (orderId: string, data: CreateOrderItemInput) =>
    foodClient.post<FoodOrderItem>(`/api/v1/orders/${orderId}/items`, data),
  mergeOrders: (orderIds: string[]) =>
    foodClient.post<FoodOrder>('/api/v1/orders/merge', { order_ids: orderIds }),
  listEvents: (id: string) =>
    foodClient.get<{ events: FoodOrderEvent[] }>(`/api/v1/orders/${id}/events`),
  transfer: (id: string, toUserId: string) =>
    foodClient.post<FoodOrder>(`/api/v1/orders/${id}/transfer`, { to_user_id: toUserId }),
  reassign: (id: string, toUserId: string) =>
    foodClient.post<FoodOrder>(`/api/v1/orders/${id}/reassign`, { to_user_id: toUserId }),
  getLifecycleReport: (params?: { start_date?: string; end_date?: string; limit?: number }) =>
    foodClient.get<OrderLifecycleReport>('/api/v1/reporting/order-lifecycle', { params }),
  getItemLifecycleReport: (params?: { start_date?: string; end_date?: string; limit?: number }) =>
    foodClient
      .get<ItemLifecycleReport>('/api/v1/reporting/item-lifecycle', { params })
      .then(r => r.data),
  getBestSellers: (params?: { start_date?: string; end_date?: string; limit?: number }) =>
    foodClient.get<{ data: FoodBestSeller[] }>('/api/v1/reporting/best-sellers', { params }),
  getFoodTimingReport: (params?: { start_date?: string; end_date?: string }) =>
    foodClient.get<FoodTimingReport>('/api/v1/reporting/food-timing', { params }).then(r => r.data),
};

export interface FoodBestSeller {
  product_name: string;
  product_id: string;
  quantity_sold: number;
  total_revenue: number;
  order_count: number;
}

export interface FoodTimingItem {
  product_name: string;
  product_id: string;
  time_period: string;
  day_of_week: string;
  day_number: number;
  quantity_sold: number;
  order_count: number;
  total_revenue: number;
  avg_prep_minutes: number;
  avg_serve_minutes: number;
  avg_total_minutes: number;
}

export interface FoodTimingReport {
  items: FoodTimingItem[];
  start_time: string;
  end_time: string;
}
